/**
 * VaultSheetEditor — the spreadsheet surface for .xlsx / .xlsm / .csv.
 *
 * Everything a person expects from a sheet and nothing that would lie about
 * what the file can hold:
 *
 *   selection    click, shift-click and drag select a range; the toolbar acts
 *                on every cell in it
 *   formatting   bold, italic, underline, text colour, fill colour, alignment
 *                — carried into the real .xlsx cell by cell
 *   structure    insert / delete rows and columns at a position, not just at
 *                the end; add, rename, reorder-free delete of sheet tabs
 *   formulas     =SUM(A1:A9), =B2*1.17, =AVERAGE(...) evaluated live for
 *                display and stored as real formulas in the workbook
 *   keyboard     Enter / Tab / arrows / Escape / Delete, Ctrl+B, Ctrl+I, Ctrl+U
 *
 * CSV files use the same grid with the formatting controls hidden, because a
 * .csv genuinely cannot carry a colour and showing the buttons would be a lie.
 *
 * Built without a grid dependency on purpose: node_modules here is shared with
 * other checkouts, so adding one would mutate an environment this feature does
 * not own.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popover, Modal, Input, message } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import {
  Book, Cell, Sheet, colName, displayValue, parseBook, serialiseBook, MIN_COLS,
} from './sheetModel';

const TEXT_COLOURS = [
  '000000', '434343', '666666', '999999', 'B7B7B7', 'CCCCCC', 'FFFFFF',
  'C00000', 'E06666', 'B45F06', 'BF9000', '38761D', '1155CC', '674EA7',
];
const FILL_COLOURS = [
  'FFF2CC', 'FCE5CD', 'F4CCCC', 'D9EAD3', 'D0E0E3', 'CFE2F3', 'D9D2E9',
  'FFD966', 'F6B26B', 'EA9999', '93C47D', '76A5AF', '6D9EEB', 'B4A7D6',
];

interface Sel { r1: number; c1: number; r2: number; c2: number }
const norm = (s: Sel) => ({
  top: Math.min(s.r1, s.r2), bottom: Math.max(s.r1, s.r2),
  left: Math.min(s.c1, s.c2), right: Math.max(s.c1, s.c2),
});
const inSel = (s: Sel, r: number, c: number) => {
  const n = norm(s);
  return r >= n.top && r <= n.bottom && c >= n.left && c <= n.right;
};

interface Props {
  value: string;
  readOnly?: boolean;
  /** csv hides the formatting controls; xlsx shows them. */
  kind?: 'xlsx' | 'csv';
  /** '\t' for .tsv files. */
  delimiter?: string;
  onChange: (next: string) => void;
}

export function VaultSheetEditor({ value, readOnly, kind = 'xlsx', delimiter = ',', onChange }: Props) {
  const { t } = useLanguage();
  const [book, setBook] = useState<Book>(() => parseBook(value, kind, delimiter));
  const [active, setActive] = useState(0);
  const [sel, setSel] = useState<Sel>({ r1: 0, c1: 0, r2: 0, c2: 0 });
  const [focused, setFocused] = useState<{ r: number; c: number } | null>(null);
  const [renaming, setRenaming] = useState<{ index: number; name: string } | null>(null);
  // Which colour picker is open. Controlled so that choosing a colour closes it,
  // the way every other spreadsheet behaves — leaving it open forces a person to
  // dismiss it, and Escape would close the whole file window with it.
  const [picker, setPicker] = useState<null | 'fg' | 'bg'>(null);
  const dragging = useRef(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const lastPushed = useRef(value);
  const rich = kind === 'xlsx';

  // Re-parse only when the document genuinely changed underneath us (a reload),
  // never on our own edits — that would fight the person's cursor.
  useEffect(() => {
    if (value !== lastPushed.current) {
      setBook(parseBook(value, kind, delimiter));
      lastPushed.current = value;
    }
  }, [value, kind, delimiter]);

  useEffect(() => {
    const up = () => { dragging.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const push = useCallback((sheets: Sheet[]) => {
    const next: Book = { kind, sheets };
    setBook(next);
    const text = serialiseBook(next, delimiter);
    lastPushed.current = text;
    onChange(text);
  }, [kind, delimiter, onChange]);

  const sheet = book.sheets[active] || { name: '', rows: [[]] };
  const rows = sheet.rows;
  const cols = useMemo(
    () => Math.max(MIN_COLS, ...rows.map((r) => r.length)),
    [rows],
  );

  /** Replace the active sheet's rows, leaving every other sheet alone. */
  const setRows = (next: (Cell | null)[][]) =>
    push(book.sheets.map((s, i) => (i === active ? { ...s, rows: next } : s)));

  const cellAt = (r: number, c: number): Cell | null => rows[r]?.[c] ?? null;

  const writeCells = (mutate: (cell: Cell | null, r: number, c: number) => Cell | null) => {
    const n = norm(sel);
    const next = rows.map((row, r) => {
      if (r < n.top || r > n.bottom) return row;
      const copy = row.slice();
      while (copy.length <= n.right) copy.push(null);
      for (let c = n.left; c <= n.right; c += 1) copy[c] = mutate(copy[c] ?? null, r, c);
      return copy;
    });
    while (next.length <= n.bottom) next.push(new Array(cols).fill(null));
    setRows(next);
  };

  const setCell = (r: number, c: number, v: string) => {
    const next = rows.map((row) => row.slice());
    while (next.length <= r) next.push(new Array(cols).fill(null));
    while (next[r].length <= c) next[r].push(null);
    const prev = next[r][c] ?? {};
    next[r][c] = v === '' && !prev.b && !prev.i && !prev.u && !prev.fg && !prev.bg && !prev.a
      ? null
      : { ...prev, v };
    setRows(next);
  };

  // -- formatting -----------------------------------------------------------

  const toggle = (key: 'b' | 'i' | 'u') => {
    const current = !!cellAt(norm(sel).top, norm(sel).left)?.[key];
    writeCells((cell) => {
      const next: Cell = { ...(cell ?? {}) };
      if (current) delete next[key];
      else next[key] = 1;
      return Object.keys(next).length ? next : null;
    });
  };

  const paint = (key: 'fg' | 'bg', colour: string | null) => {
    setPicker(null);
    writeCells((cell) => {
      const next: Cell = { ...(cell ?? {}) };
      if (colour) next[key] = colour;
      else delete next[key];
      return Object.keys(next).length ? next : null;
    });
  };

  const align = (how: 'left' | 'center' | 'right' | null) =>
    writeCells((cell) => {
      const next: Cell = { ...(cell ?? {}) };
      if (how) next.a = how;
      else delete next.a;
      return Object.keys(next).length ? next : null;
    });

  const clearContents = () =>
    writeCells((cell) => {
      if (!cell) return null;
      const next = { ...cell };
      delete next.v;
      return Object.keys(next).length ? next : null;
    });

  // -- structure ------------------------------------------------------------

  const insertRow = (where: 'above' | 'below') => {
    const n = norm(sel);
    const at = where === 'above' ? n.top : n.bottom + 1;
    const next = rows.slice();
    next.splice(at, 0, new Array(cols).fill(null));
    setRows(next);
    setSel({ r1: at, c1: n.left, r2: at, c2: n.left });
  };

  const deleteRow = () => {
    const n = norm(sel);
    if (rows.length - (n.bottom - n.top + 1) < 1) return;
    const next = rows.slice();
    next.splice(n.top, n.bottom - n.top + 1);
    setRows(next);
    setSel({ r1: Math.max(0, n.top - 1), c1: n.left, r2: Math.max(0, n.top - 1), c2: n.left });
  };

  const insertCol = (where: 'left' | 'right') => {
    const n = norm(sel);
    const at = where === 'left' ? n.left : n.right + 1;
    setRows(rows.map((row) => {
      const copy = row.slice();
      while (copy.length < cols) copy.push(null);
      copy.splice(at, 0, null);
      return copy;
    }));
    setSel({ r1: n.top, c1: at, r2: n.top, c2: at });
  };

  const deleteCol = () => {
    const n = norm(sel);
    if (cols - (n.right - n.left + 1) < 1) return;
    setRows(rows.map((row) => {
      const copy = row.slice();
      while (copy.length < cols) copy.push(null);
      copy.splice(n.left, n.right - n.left + 1);
      return copy;
    }));
    setSel({ r1: n.top, c1: Math.max(0, n.left - 1), r2: n.top, c2: Math.max(0, n.left - 1) });
  };

  // -- sheet tabs -----------------------------------------------------------

  const addSheet = () => {
    const used = new Set(book.sheets.map((s) => s.name));
    let n = book.sheets.length + 1;
    while (used.has(`Sheet${n}`)) n += 1;
    push([...book.sheets, { name: `Sheet${n}`, rows: parseBook('', 'csv').sheets[0].rows }]);
    setActive(book.sheets.length);
    setSel({ r1: 0, c1: 0, r2: 0, c2: 0 });
  };

  const deleteSheet = (index: number) => {
    if (book.sheets.length < 2) {
      message.warning(t('vault.viewer.lastSheet'));
      return;
    }
    push(book.sheets.filter((_, i) => i !== index));
    setActive((a) => (a >= index && a > 0 ? a - 1 : a));
  };

  const commitRename = () => {
    if (!renaming) return;
    const name = renaming.name.trim();
    const clash = book.sheets.some((s, i) => i !== renaming.index && s.name === name);
    if (!name || clash) {
      message.warning(t('vault.viewer.sheetNameTaken'));
      return;
    }
    push(book.sheets.map((s, i) => (i === renaming.index ? { ...s, name } : s)));
    setRenaming(null);
  };

  // -- keyboard -------------------------------------------------------------

  const focusCell = (r: number, c: number) => {
    const el = gridRef.current?.querySelector<HTMLInputElement>(`input[data-r="${r}"][data-c="${c}"]`);
    el?.focus();
    el?.select();
    setSel({ r1: r, c1: c, r2: r, c2: c });
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>, r: number, c: number) => {
    const lastRow = rows.length - 1;
    const go = (nr: number, nc: number) => {
      e.preventDefault();
      focusCell(Math.max(0, Math.min(nr, lastRow)), Math.max(0, Math.min(nc, cols - 1)));
    };
    if ((e.ctrlKey || e.metaKey) && rich && !readOnly) {
      const key = e.key.toLowerCase();
      if (key === 'b' || key === 'i' || key === 'u') {
        e.preventDefault();
        toggle(key as 'b' | 'i' | 'u');
        return;
      }
    }
    if (e.shiftKey && e.key.startsWith('Arrow')) {
      // Extend the selection rather than moving, the way a sheet does.
      e.preventDefault();
      const hStep = document.documentElement.dir === 'rtl' ? -1 : 1;
      const d = { ArrowDown: [1, 0], ArrowUp: [-1, 0], ArrowRight: [0, hStep], ArrowLeft: [0, -hStep] }[e.key];
      if (d) {
        setSel((s) => ({
          ...s,
          r2: Math.max(0, Math.min(s.r2 + d[0], lastRow)),
          c2: Math.max(0, Math.min(s.c2 + d[1], cols - 1)),
        }));
      }
      return;
    }
    if (e.key === 'Enter') go(r + 1, c);
    else if (e.key === 'Tab') go(r, e.shiftKey ? c - 1 : c + 1);
    else if (e.key === 'ArrowDown') go(r + 1, c);
    else if (e.key === 'ArrowUp') go(r - 1, c);
    else if (e.key === 'Escape') (e.target as HTMLInputElement).blur();
    else if (e.key === 'Delete' && !readOnly) {
      const n = norm(sel);
      if (n.top !== n.bottom || n.left !== n.right) { e.preventDefault(); clearContents(); }
    }
  };

  // -- rendering ------------------------------------------------------------

  const activeCell = cellAt(norm(sel).top, norm(sel).left);
  const selLabel = (() => {
    const n = norm(sel);
    return n.top === n.bottom && n.left === n.right
      ? `${colName(n.left)}${n.top + 1}`
      : `${colName(n.left)}${n.top + 1}:${colName(n.right)}${n.bottom + 1}`;
  })();

  const swatches = (key: 'fg' | 'bg', list: string[]) => (
    <div className="vault-swatches">
      {list.map((hex) => (
        <button
          key={hex}
          type="button"
          className="vault-swatch"
          style={{ background: `#${hex}` }}
          aria-label={`#${hex}`}
          onClick={() => paint(key, hex)}
        />
      ))}
      <button type="button" className="vault-swatch none" onClick={() => paint(key, null)}>
        {t('vault.viewer.noColour')}
      </button>
    </div>
  );

  const tool = (label: string, title: string, onClick: () => void, activeState = false, cls = '') => (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={activeState}
      className={`vault-tool ${cls} ${activeState ? 'on' : ''}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <div className="vault-sheet">
      {!readOnly && (
        <div className="vault-sheet-toolbar">
          {rich && (
            <>
              <span className="vault-tool-group">
                {tool('B', t('vault.viewer.bold'), () => toggle('b'), !!activeCell?.b, 'bold')}
                {tool('I', t('vault.viewer.italic'), () => toggle('i'), !!activeCell?.i, 'italic')}
                {tool('U', t('vault.viewer.underline'), () => toggle('u'), !!activeCell?.u, 'under')}
              </span>
              <span className="vault-tool-group">
                <Popover
                  trigger="click"
                  placement="bottom"
                  open={picker === 'fg'}
                  onOpenChange={(o) => setPicker(o ? 'fg' : null)}
                  content={swatches('fg', TEXT_COLOURS)}
                  title={t('vault.viewer.textColour')}
                >
                  <button
                    type="button"
                    className="vault-tool swatchy"
                    title={t('vault.viewer.textColour')}
                    aria-label={t('vault.viewer.textColour')}
                  >
                    A<i style={{ background: `#${activeCell?.fg || '000000'}` }} />
                  </button>
                </Popover>
                <Popover
                  trigger="click"
                  placement="bottom"
                  open={picker === 'bg'}
                  onOpenChange={(o) => setPicker(o ? 'bg' : null)}
                  content={swatches('bg', FILL_COLOURS)}
                  title={t('vault.viewer.fillColour')}
                >
                  <button
                    type="button"
                    className="vault-tool swatchy"
                    title={t('vault.viewer.fillColour')}
                    aria-label={t('vault.viewer.fillColour')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 14 12 6l6 6-8 8z" /><path d="M19 15c0 1.7-1 3-2 3s-2-1.3-2-3 2-3 2-3 2 1.3 2 3z" />
                    </svg>
                    <i style={{ background: `#${activeCell?.bg || 'FFFFFF'}` }} />
                  </button>
                </Popover>
              </span>
              <span className="vault-tool-group">
                {tool('⯇', t('vault.viewer.alignLeft'), () => align('left'), activeCell?.a === 'left')}
                {tool('⯈', t('vault.viewer.alignCentre'), () => align('center'), activeCell?.a === 'center')}
                {tool('⯆', t('vault.viewer.alignRight'), () => align('right'), activeCell?.a === 'right')}
              </span>
            </>
          )}
          <span className="vault-tool-group">
            {tool('+↑', t('vault.viewer.insertRowAbove'), () => insertRow('above'))}
            {tool('+↓', t('vault.viewer.insertRowBelow'), () => insertRow('below'))}
            {tool('−', t('vault.viewer.deleteRow'), deleteRow)}
          </span>
          <span className="vault-tool-group">
            {tool('+←', t('vault.viewer.insertColLeft'), () => insertCol('left'))}
            {tool('+→', t('vault.viewer.insertColRight'), () => insertCol('right'))}
            {tool('−', t('vault.viewer.deleteColumn'), deleteCol)}
          </span>
          <span className="vault-tool-group">
            {tool('⌫', t('vault.viewer.clearCells'), clearContents)}
          </span>
        </div>
      )}

      <div className="vault-formula-bar">
        <span className="vault-cell-ref">{selLabel}</span>
        <span className="vault-fx">fx</span>
        <input dir="auto"
          className="vault-formula-input"
          value={activeCell?.v ?? ''}
          readOnly={readOnly}
          placeholder={t('vault.viewer.formulaHint')}
          onChange={(e) => setCell(norm(sel).top, norm(sel).left, e.target.value)}
        />
      </div>

      <div className="vault-sheet-scroll" ref={gridRef}>
        <table className="vault-sheet-grid">
          <thead>
            <tr>
              <th className="corner" />
              {Array.from({ length: cols }, (_, c) => (
                <th
                  key={c}
                  className={inSel(sel, norm(sel).top, c) ? 'hi' : ''}
                  onMouseDown={() => setSel({ r1: 0, c1: c, r2: rows.length - 1, c2: c })}
                >
                  {colName(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                <th
                  className={`rownum ${inSel(sel, r, norm(sel).left) ? 'hi' : ''}`}
                  onMouseDown={() => setSel({ r1: r, c1: 0, r2: r, c2: cols - 1 })}
                >
                  {r + 1}
                </th>
                {Array.from({ length: cols }, (_, c) => {
                  const cell = row[c] ?? null;
                  const isFocused = focused?.r === r && focused?.c === c;
                  const n = norm(sel);
                  const anchor = r === n.top && c === n.left;
                  return (
                    <td
                      key={c}
                      className={`${inSel(sel, r, c) ? 'in-sel' : ''} ${anchor ? 'sel' : ''}`}
                      style={cell?.bg ? { background: `#${cell.bg}` } : undefined}
                      onMouseDown={(e) => {
                        dragging.current = true;
                        if (e.shiftKey) { e.preventDefault(); setSel((s) => ({ ...s, r2: r, c2: c })); }
                        else setSel({ r1: r, c1: c, r2: r, c2: c });
                      }}
                      onMouseEnter={() => {
                        if (dragging.current) setSel((s) => ({ ...s, r2: r, c2: c }));
                      }}
                    >
                      <input dir="auto"
                        data-r={r}
                        data-c={c}
                        // Focused shows the raw formula; unfocused shows its result,
                        // exactly the way a spreadsheet behaves.
                        value={isFocused ? cell?.v ?? '' : displayValue(cell, rows)}
                        readOnly={readOnly}
                        style={{
                          fontWeight: cell?.b ? 700 : undefined,
                          fontStyle: cell?.i ? 'italic' : undefined,
                          textDecoration: cell?.u ? 'underline' : undefined,
                          color: cell?.fg ? `#${cell.fg}` : undefined,
                          textAlign: cell?.a,
                        }}
                        onFocus={() => { setFocused({ r, c }); setSel({ r1: r, c1: c, r2: r, c2: c }); }}
                        onBlur={() => setFocused((f) => (f?.r === r && f?.c === c ? null : f))}
                        onChange={(e) => setCell(r, c, e.target.value)}
                        onKeyDown={(e) => onKey(e, r, c)}
                        aria-label={`${colName(c)}${r + 1}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rich && (
        <div className="vault-sheet-tabs" role="tablist">
          {book.sheets.map((s, i) => (
            <span key={`${s.name}-${i}`} className={`vault-sheet-tab ${i === active ? 'active' : ''}`}>
              <button
                type="button"
                role="tab"
                aria-selected={i === active}
                onClick={() => { setActive(i); setSel({ r1: 0, c1: 0, r2: 0, c2: 0 }); }}
                onDoubleClick={() => !readOnly && setRenaming({ index: i, name: s.name })}
              >
                {s.name}
              </button>
              {!readOnly && book.sheets.length > 1 && (
                <button
                  type="button"
                  className="vault-tab-x"
                  title={t('vault.viewer.deleteSheet')}
                  aria-label={`${t('vault.viewer.deleteSheet')}: ${s.name}`}
                  onClick={() => deleteSheet(i)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {!readOnly && (
            <button type="button" className="vault-sheet-add" title={t('vault.viewer.addSheet')} onClick={addSheet}>
              +
            </button>
          )}
          {/* Instructions for typing, where typing is possible. */}
          {!readOnly && (
            <span className="vault-sheet-hint">{t('vault.viewer.sheetHint')}</span>
          )}
        </div>
      )}

      <Modal
        open={!!renaming}
        title={t('vault.viewer.renameSheet')}
        onCancel={() => setRenaming(null)}
        onOk={commitRename}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Input dir="auto"
          value={renaming?.name ?? ''}
          maxLength={31}
          onChange={(e) => setRenaming((s) => (s ? { ...s, name: e.target.value } : s))}
          onPressEnter={commitRename}
        />
      </Modal>
    </div>
  );
}

export default VaultSheetEditor;
